import { QueryClientProvider } from '@tanstack/react-query';
import { App as AntdApp, ConfigProvider, Flex, Spin } from 'antd';
import viVN from 'antd/locale/vi_VN';
import dayjs from 'dayjs';
import 'dayjs/locale/vi';
import { useEffect, useMemo } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { onUnauthenticated } from '@/api/client';
import { AuthPage } from '@/features/auth/AuthPage';
import { ForcedPasswordChange } from '@/features/auth/ForcedPasswordChange';
import { useSessionStore } from '@/stores/session-store';
import { queryClient } from './query-client';
import { AppRoutes } from './router';
import { FONT_FAMILY, buildAntdTheme, useThemeStore } from './theme';

dayjs.locale('vi');

function SessionGate() {
  const status = useSessionStore((state) => state.status);
  const mustChangePassword = useSessionStore((state) => state.user?.mustChangePassword ?? false);
  const initialize = useSessionStore((state) => state.initialize);
  const expire = useSessionStore((state) => state.expire);

  useEffect(() => {
    void initialize();
    // `expire` chạy mọi hàm dọn theo phiên, gồm cả xoá bộ nhớ đệm (`query-client.ts`).
    return onUnauthenticated(expire);
  }, [initialize, expire]);

  if (status === 'loading') {
    return (
      <Flex align="center" justify="center" style={{ minHeight: '100dvh' }}>
        <Spin size="large" />
      </Flex>
    );
  }
  if (status === 'anonymous') return <AuthPage />;
  if (mustChangePassword) return <ForcedPasswordChange />;
  return <AppRoutes />;
}

export function App() {
  const mode = useThemeStore((state) => state.mode);
  const syncWithSystem = useThemeStore((state) => state.syncWithSystem);
  const antdTheme = useMemo(() => buildAntdTheme(mode), [mode]);

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    media.addEventListener('change', syncWithSystem);
    return () => media.removeEventListener('change', syncWithSystem);
  }, [syncWithSystem]);

  useEffect(() => {
    document.documentElement.style.colorScheme = mode;
    document.body.style.background = antdTheme.token?.colorBgLayout ?? '';
    // Ant Design chỉ đặt font cho THÀNH PHẦN của nó; chữ nằm ngoài (thẻ div/span trần) thừa hưởng từ body.
    document.body.style.fontFamily = FONT_FAMILY;
  }, [mode, antdTheme]);

  // `variant="filled"`: ô nhập / ô chọn nền nhạt không viền — mềm hơn ô viền đen, viền chỉ hiện khi đang gõ.
  return (
    <ConfigProvider locale={viVN} theme={antdTheme} variant="filled">
      <AntdApp>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <SessionGate />
          </BrowserRouter>
        </QueryClientProvider>
      </AntdApp>
    </ConfigProvider>
  );
}
